import { Alert } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { Loader } from '../../../components/Loader/Loader';
import {
  CompanyPeriodRequestStatus,
  DataPointPeriodicity,
} from '../../../schemas/CompanyPeriodRequest.schema';
import { kpiTemplatesMapByUuidStateLoadableJ } from '../../../services/state/KPI/KPITemplatesStateJ';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { usePortCos } from '../../Finance2/hooks/usePortCos';
import { CompanyPeriodRequestManageV2 } from './CompanyPeriodRequestManageV2';
import { useCompanyPeriodRequestDataV2 } from './company-period-request-data-v2';
import {
  ALL_PERIODS,
  AvailablePeriod,
  getV2CompanyPeriodRequestStatuses,
  mapV2CompanyPeriodRequestsToViewModel,
} from './manage-request-utils';

export function CompanyPeriodRequestManageLoaderV2() {
  const [selectedFrequency, setSelectedFrequency] = useState<DataPointPeriodicity>(
    DataPointPeriodicity.quarter
  );
  const [selectedPeriod, setSelectedPeriod] = useState<string>(ALL_PERIODS);

  const { loading: loadingPortcos, error: portcosError, data: portcos } = usePortCos();
  const {
    isLoading: isLoadingRequests,
    error: requestsError,
    data: requestResponse,
  } = useCompanyPeriodRequestDataV2();
  const templatesLoadable = useAtomValue(kpiTemplatesMapByUuidStateLoadableJ);
  const templatesByUuid = templatesLoadable.state === 'hasData' ? templatesLoadable.data : undefined;

  const allStatuses = useMemo(() => getV2CompanyPeriodRequestStatuses(), []);

  const onFrequencyChange = (frequency: DataPointPeriodicity) => {
    setSelectedFrequency(frequency);
    // A period from the previous frequency is meaningless under the new one.
    setSelectedPeriod(ALL_PERIODS);
  };

  const { requests, requestStats, availablePeriods } = useMemo(() => {
    if (portcos && requestResponse?.success) {
      return mapV2CompanyPeriodRequestsToViewModel({
        universalRequests: requestResponse.universal.requests,
        firmRequests: requestResponse.firm.requests,
        companies: portcos,
        selectedFrequency,
        selectedPeriod,
        templatesByUuid,
      });
    }
    return {
      requests: [],
      requestStats: new Map<CompanyPeriodRequestStatus | string, number>(),
      availablePeriods: [] as AvailablePeriod[],
    };
  }, [requestResponse, portcos, selectedFrequency, selectedPeriod, templatesByUuid]);

  if (loadingPortcos || isLoadingRequests) {
    return <Loader />;
  }

  if (portcosError || requestsError || !requestResponse?.success) {
    return (
      <Alert severity='error' sx={{ width: '100%', height: 'fit-content' }}>
        Error loading data.
      </Alert>
    );
  }

  if (!portcos || !requestResponse) {
    return <NoDataMessage2 />;
  }

  return (
    <CompanyPeriodRequestManageV2
      allStatuses={allStatuses}
      requestStats={requestStats}
      availablePeriods={availablePeriods}
      requests={requests}
      selectedFrequency={selectedFrequency}
      onFrequencyChange={onFrequencyChange}
      selectedPeriod={selectedPeriod}
      onPeriodChange={setSelectedPeriod}
    />
  );
}
